/**
 * GridProvisioningService — one-click, first-time setup of the Production
 * Grid's SharePoint lists on the NEW Manufacturing site (see mfgSite.ts).
 *
 * Creates iSyn_Batches, iSyn_WIPs (with the BatchID lookup), and
 * iSyn_StageHistory with exactly the schema ProductionGridService reads and
 * writes, plus the two single-row JSON lists (iSyn_GridSettings /
 * iSyn_GridDailyOutput).
 *
 * SAFETY: this is NOT the historical schema-self-provisioning that polluted
 * the legacy iSyn_WIPs list — that ran silently on page load against an
 * existing, populated list. provision() (a) only runs when the user clicks
 * "Set up lists", (b) only creates a list when it does NOT exist, and (c)
 * never adds fields to a list it didn't just create — on a brand-new site
 * there is no existing schema to collide with.
 *
 * missingFields()/addMissingFields() are the deliberate, explicit exception
 * to (c): a genuinely new field the spec has grown since a site's lists were
 * first created (see the BQC1 columns added for the Control Tower mother
 * record — joSeries, wkSeries, dateAssignedToCutter, inboundedToRtgStockroom,
 * packed, cancellationStatus, directMatl, cogm) still has to reach an
 * already-live iSyn_WIPs somehow. Only ever additive, only ever run
 * explicitly from Production Grid Settings → Maintenance, never touches a
 * field that's already there.
 *
 * All fields are created non-required on purpose: SharePoint required-field
 * validation adds nothing here (the import always writes complete rows) and
 * only creates avoidable 400s.
 */

import { createSharePointClient } from "./spClient";
import { MFG_SITE_URL } from "./mfgSite";

type FieldSpec =
  | { name: string; kind: "text"; indexed?: boolean }
  | { name: string; kind: "note" }
  | { name: string; kind: "number" }
  | { name: string; kind: "datetime"; indexed?: boolean }
  | { name: string; kind: "boolean" }
  | { name: string; kind: "choice"; choices: string[] }
  | { name: string; kind: "lookup"; lookupList: string; indexed?: boolean };

interface ListSpec {
  title: string;
  description: string;
  fields: FieldSpec[];
}

/** Field type kinds per SP.FieldType. */
const KIND: Record<string, number> = { text: 2, note: 3, choice: 6, number: 9, datetime: 4, lookup: 7, boolean: 8 };

const STAGE_CHOICES = ["BQC1", "BQC2", "BQC3", "BQC4", "BQC5"];

export const LIST_SPECS: ListSpec[] = [
  {
    title: "iSyn_Batches",
    description: "Production Grid (Excel-import version) — one row per batch. Provisioned by the i-Synergy app.",
    fields: [
      { name: "wkSeries", kind: "text" },
      { name: "dateOrdered", kind: "datetime" },
      { name: "prodPlan", kind: "number" },
      { name: "cutTotal", kind: "number" },
      { name: "assignedTotal", kind: "number" },
      { name: "receivedTotal", kind: "number" },
      { name: "qcTotal", kind: "number" },
      { name: "batchStatus", kind: "choice", choices: ["Active", "Completed", "Stale", "Cancelled"] },
      { name: "notes", kind: "note" },
    ],
  },
  {
    title: "iSyn_WIPs",
    description: "Production Grid (Excel-import version) — one row per WIP. Provisioned by the i-Synergy app.",
    fields: [
      // Indexed: with 7k+ WIPs, any $orderby/$filter on these columns hits
      // SharePoint's 5,000-item list view threshold unless they're indexed.
      { name: "wipNumber", kind: "text", indexed: true },
      { name: "orderId", kind: "text" },
      { name: "serialId", kind: "text" },
      { name: "cutter", kind: "text" },
      { name: "design", kind: "text" },
      { name: "sku", kind: "text" },
      { name: "category", kind: "choice", choices: ["CLASSIC", "ELITE", "ALTA", "PRIMO", "ELITE PRO"] },
      { name: "qty", kind: "number" },
      { name: "currentStage", kind: "choice", choices: [...STAGE_CHOICES, "DONE", "LOST", "DAMAGED"] },
      { name: "dateCreated", kind: "datetime" },
      { name: "dueDate", kind: "datetime" },
      { name: "storeChannel", kind: "choice", choices: ["LAZ IG", "SHP IG", "TIKTOK", "IGCOM", "OTHER"] },
      { name: "notes", kind: "note" },
      { name: "BatchID", kind: "lookup", lookupList: "iSyn_Batches", indexed: true },
      { name: "AssignedSewer", kind: "text" },
      { name: "color", kind: "text" },
      { name: "size", kind: "text" },
      { name: "assignedDS", kind: "text" },
      { name: "deliveryStatus", kind: "text" },
      { name: "receivedQty", kind: "number" },
      { name: "dispatchedAt", kind: "datetime" },
      { name: "receivedAt", kind: "datetime" },
      { name: "cutOperator", kind: "text" },
      // BQC1's own columns — added so iSyn_WIPs carries the mother record
      // every other BQC can look up against, not just the fields the earlier
      // stage-tracking columns above happened to need.
      { name: "joSeries", kind: "text" },
      { name: "wkSeries", kind: "text" },
      { name: "dateAssignedToCutter", kind: "datetime" },
      { name: "inboundedToRtgStockroom", kind: "boolean" },
      { name: "packed", kind: "boolean" },
      { name: "cancellationStatus", kind: "choice", choices: ["none", "requested", "cancelled"] },
      { name: "directMatl", kind: "number" },
      { name: "cogm", kind: "number" },
      // BQC4's own GRN column — the clerk's real Goods Receipt Number, read
      // via XLOOKUP against RECEIVING USERFORM's own RECEIVING sheet
      // (2026-08-18 design conversation with Mikko). See
      // parseProductionGridWorkbook.ts and receivingLedger.ts's
      // historicalReceiveTally.
      { name: "grn", kind: "text" },
      // BQC5's own reconciled passed-sets figure and date (2026-08-19,
      // mirroring grn/receivedQty above for QC's side of the same fix). See
      // parseProductionGridWorkbook.ts and qcLedger.ts's historicalQcTally.
      { name: "qcPassedQty", kind: "number" },
      { name: "qcPassedAt", kind: "datetime" },
      { name: "cutAt", kind: "datetime" },
      // The rest of what BQC5 records about a WIP: who inspected it, and how
      // many sets the REPAIR sheet sent back. Both were being read off the
      // workbook and then dropped on the floor, because iSyn_WIPs had nowhere
      // to put them — so QC · BQC5 showed an em-dash and a 0 on every bundle
      // inspected before this station existed. See IProductionWip's own field
      // comments for why qcRepairQty is an event tally rather than a state.
      { name: "qcPersonnel", kind: "text" },
      { name: "qcRepairQty", kind: "number" },
    ],
  },
  {
    title: "iSyn_StageHistory",
    description: "Production Grid (Excel-import version) — append-only per-WIP stage transition log. Provisioned by the i-Synergy app.",
    fields: [
      // Indexed: the history table grows past 25k rows — filters on wipId
      // (Batch Detail) and orderby/filter on transitionAt (KPI anchor) need
      // indexes to clear the list view threshold.
      { name: "wipId", kind: "text", indexed: true },
      { name: "fromStage", kind: "choice", choices: STAGE_CHOICES },
      { name: "toStage", kind: "choice", choices: [...STAGE_CHOICES, "DONE"] },
      { name: "transitionAt", kind: "datetime", indexed: true },
      { name: "operator", kind: "text" },
    ],
  },
  {
    title: "iSyn_GridSettings",
    description: "Production Grid — user-customizable style settings (single JSON row).",
    fields: [{ name: "settingsJson", kind: "note" }],
  },
  {
    title: "iSyn_GridDailyOutput",
    // Only outputJson is listed. The split fields this list has grown since
    // — timingJson, sewerNamesJson, repairsJson — are created lazily by
    // GridDailyOutputService on its next save, and are deliberately left out
    // of this spec so that service stays their single creation authority.
    // Listing them here would report a gap that closes itself on the next
    // import: a diagnostic nobody can act on. Where a missing repairsJson
    // actually matters — grades falling back to provisional — Sewer Metrics
    // says so in context, which is the screen someone is already looking at.
    description: "Production Grid — per-day production totals from the BQC2-BQC5 sheets, plus lifetime repair findings per sewer (single JSON row).",
    fields: [{ name: "outputJson", kind: "note" }],
  },
];

class GridProvisioningService {
  private readonly sp = createSharePointClient(MFG_SITE_URL);

  /** Lists from the spec that do NOT exist yet on the Manufacturing site. */
  public async missingLists(): Promise<string[]> {
    const missing: string[] = [];
    for (const spec of LIST_SPECS) {
      if (!(await this.listExists(spec.title))) missing.push(spec.title);
    }
    return missing;
  }

  /** InternalName of every non-hidden, non-base-type field currently on a
   * list — the live truth to diff a spec's field names against. */
  private async listFieldNames(listTitle: string): Promise<Set<string>> {
    const res = await this.sp.raw<{ d: { results: { InternalName: string }[] } }>(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/fields?$select=InternalName&$filter=FromBaseType eq false and Hidden eq false`
    );
    return new Set(res.d.results.map(f => f.InternalName));
  }

  /**
   * For every list that EXISTS, which of the spec's fields are missing from
   * it — the gap provision() deliberately never closes (see the module
   * header). Skips lists that don't exist yet — provision() covers those.
   */
  public async missingFields(): Promise<{ list: string; fields: string[] }[]> {
    const out: { list: string; fields: string[] }[] = [];
    for (const spec of LIST_SPECS) {
      if (!(await this.listExists(spec.title))) continue;
      const have = await this.listFieldNames(spec.title);
      const missing = spec.fields.map(f => f.name).filter(n => !have.has(n));
      if (missing.length > 0) out.push({ list: spec.title, fields: missing });
    }
    return out;
  }

  /**
   * Adds ONLY the fields missing from each already-existing list — never
   * touches a field that's already there, never removes anything, never
   * creates a list. See the module header for why this is the deliberate
   * exception to "never adds fields to a list it didn't just create."
   */
  public async addMissingFields(onProgress?: (message: string) => void): Promise<{ list: string; added: string[] }[]> {
    const gaps = await this.missingFields();
    const out: { list: string; added: string[] }[] = [];
    for (const { list, fields: names } of gaps) {
      const spec = LIST_SPECS.find(s => s.title === list);
      if (!spec) continue;
      const added: string[] = [];
      for (const name of names) {
        const field = spec.fields.find(f => f.name === name);
        if (!field) continue;
        onProgress?.(`${list}: adding ${name}…`);
        await this.createField(list, field);
        added.push(name);
      }
      onProgress?.(`${list}: added ${added.join(", ")}`);
      out.push({ list, added });
    }
    return out;
  }

  /**
   * Creates every missing list with its full field set. Existing lists are
   * left completely untouched (no field reconciliation — see file header).
   * Returns the titles of the lists it created.
   */
  public async provision(onProgress?: (message: string) => void): Promise<string[]> {
    const created: string[] = [];
    for (const spec of LIST_SPECS) {
      if (await this.listExists(spec.title)) {
        onProgress?.(`${spec.title} already exists — skipped`);
        continue;
      }
      await this.createList(spec, onProgress);
      created.push(spec.title);
    }
    return created;
  }

  /** Creates one list (the SP.List row + every field, indexes included). The
   * caller guarantees the list does NOT already exist. Indexes applied here
   * always succeed because the list is empty at creation time — which is the
   * whole point of the StageHistory rebuild below. */
  private async createList(spec: ListSpec, onProgress?: (message: string) => void): Promise<void> {
    onProgress?.(`Creating ${spec.title}…`);
    await this.sp.raw(`/_api/web/lists`, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.List" },
        Title: spec.title,
        Description: spec.description,
        BaseTemplate: 100,
      }),
    });
    for (const field of spec.fields) {
      await this.createField(spec.title, field);
    }
    onProgress?.(`${spec.title} ready (${spec.fields.length} columns)`);
  }

  /**
   * Permanent fix for the un-indexable StageHistory list. Once a list passes
   * ~20k items SharePoint refuses to CREATE an index on it, so the 28k-row
   * iSyn_StageHistory can never get its wipId/transitionAt indexes — every
   * filtered query on it returns a hard 500. This recycles that list and
   * recreates it EMPTY, applying the indexes while it's small (which SP allows);
   * the index is then maintained for free as the list grows past 20k again.
   *
   * SAFE + REVERSIBLE: the old list goes to the site Recycle Bin (recoverable
   * for ~93 days), and StageHistory is a pure projection of the workbook —
   * every row is re-derived from the BQC cut/assign/receive/QC dates on the
   * next Import — so no unique data is lost. The caller MUST run an Import
   * afterwards to repopulate (the grid prompts for this).
   */
  public async rebuildStageHistoryIndexed(onProgress?: (message: string) => void): Promise<void> {
    const spec = LIST_SPECS.find((s) => s.title === "iSyn_StageHistory");
    if (!spec) throw new Error("iSyn_StageHistory spec missing");

    if (await this.listExists(spec.title)) {
      onProgress?.("Moving the old stage-history list to the Recycle Bin…");
      // /recycle (not DELETE) so it's recoverable if anything looks wrong.
      await this.sp.raw(
        `/_api/web/lists/getbytitle('${encodeURIComponent(spec.title)}')/recycle`,
        { method: "POST" }
      );
      // SharePoint frees the title asynchronously; wait for it to disappear so
      // the re-create doesn't collide with the just-recycled list.
      for (let i = 0; i < 10 && (await this.listExists(spec.title)); i++) {
        await new Promise((r) => setTimeout(r, 1000));
      }
    }

    onProgress?.("Creating a fresh, indexed stage-history list…");
    await this.createList(spec, onProgress);
    onProgress?.("Stage-history list rebuilt with indexes — run an Import to refill it.");
  }

  private async listExists(title: string): Promise<boolean> {
    try {
      await this.sp.raw(`/_api/web/lists/getbytitle('${encodeURIComponent(title)}')?$select=Id`);
      return true;
    } catch (err) {
      const msg = (err as Error).message ?? "";
      if (/404|does not exist|Invalid List Name/i.test(msg)) return false;
      throw err;
    }
  }

  private async createField(listTitle: string, field: FieldSpec): Promise<void> {
    const fieldsUrl = `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/fields`;

    if (field.kind === "lookup") {
      // Lookups need the target list's GUID and go through /fields/addfield.
      const target = await this.sp.raw<{ d: { Id: string } }>(
        `/_api/web/lists/getbytitle('${encodeURIComponent(field.lookupList)}')?$select=Id`
      );
      await this.sp.raw(`${fieldsUrl}/addfield`, {
        method: "POST",
        body: JSON.stringify({
          parameters: {
            __metadata: { type: "SP.FieldCreationInformation" },
            FieldTypeKind: KIND.lookup,
            Title: field.name,
            LookupListId: target.d.Id,
            LookupFieldName: "Title",
          },
        }),
      });
      if (field.indexed) await this.setIndexed(listTitle, field.name);
      return;
    }

    if (field.kind === "choice") {
      await this.sp.raw(fieldsUrl, {
        method: "POST",
        body: JSON.stringify({
          __metadata: { type: "SP.FieldChoice" },
          Title: field.name,
          FieldTypeKind: KIND.choice,
          Required: false,
          Choices: { __metadata: { type: "Collection(Edm.String)" }, results: field.choices },
        }),
      });
      return;
    }

    await this.sp.raw(fieldsUrl, {
      method: "POST",
      body: JSON.stringify({
        __metadata: { type: "SP.Field" },
        Title: field.name,
        FieldTypeKind: KIND[field.kind],
        Required: false,
      }),
    });
    if ((field.kind === "text" || field.kind === "datetime") && field.indexed) {
      await this.setIndexed(listTitle, field.name);
    }
  }

  /** Marks a field as indexed (idempotent MERGE). */
  private async setIndexed(listTitle: string, fieldName: string): Promise<void> {
    await this.sp.raw(
      `/_api/web/lists/getbytitle('${encodeURIComponent(listTitle)}')/fields/getbyinternalnameortitle('${encodeURIComponent(fieldName)}')`,
      {
        method: "POST",
        headers: { "If-Match": "*", "X-HTTP-Method": "MERGE" },
        body: JSON.stringify({ __metadata: { type: "SP.Field" }, Indexed: true }),
      }
    );
  }

  /**
   * Ensures the query-critical indexes exist on ALREADY-CREATED lists (the
   * first provisioning shipped without them). Safe + idempotent: indexing is
   * a metadata flag on an existing column — no fields are added or renamed.
   * NOTE: SharePoint Online refuses to create an index on a list with more
   * than ~20k items; failures are collected (not thrown) so one oversized
   * list doesn't block the others.
   */
  public async ensureIndexes(onProgress?: (message: string) => void): Promise<string[]> {
    const failures: string[] = [];
    const targets: { list: string; field: string }[] = [];
    for (const spec of LIST_SPECS) {
      for (const f of spec.fields) {
        if ("indexed" in f && f.indexed) targets.push({ list: spec.title, field: f.name });
      }
    }
    for (const t of targets) {
      try {
        await this.setIndexed(t.list, t.field);
        onProgress?.(`Indexed ${t.list}.${t.field}`);
      } catch (e) {
        failures.push(`${t.list}.${t.field}: ${(e as Error).message}`);
      }
    }
    return failures;
  }
}

export const gridProvisioningService = new GridProvisioningService();
